import { useContext } from "react";
import axios from "axios";
import { Context } from "../Reducers/Store";

const EXPIRY_BUFFER_SECONDS = 30;

let isRenewing = false;
let pendingRequests = [];

function resolvePending(token) {
  pendingRequests.forEach((cb) => cb(token));
  pendingRequests = [];
}

async function performRefresh(currentAccessToken, refreshToken, dispatch) {
  if (isRenewing) {
    return new Promise((resolve) => pendingRequests.push(resolve));
  }

  isRenewing = true;
  try {
    const config = {
      metadata: { ignoreLoading: true, skipAuth: true },
    };

    if (currentAccessToken) {
      config.headers = { Authorization: `Bearer ${currentAccessToken}` };
    }

    const res = await axios.post(
      `${process.env.REACT_APP_API}/auth/refresh`,
      { refreshToken },
      config
    );
    const newToken = res.data?.accessToken ?? null;
    if (newToken) {
      dispatch({ type: "SET_LOGGED", payload: res.data });
    } else {
      dispatch({ type: "SET_LOGGED", payload: null });
    }
    resolvePending(newToken);
    return newToken;
  } catch {
    dispatch({ type: "SET_LOGGED", payload: null });
    resolvePending(null);
    return null;
  } finally {
    isRenewing = false;
  }
}

export function useTokenHelper() {
  const [state, dispatch] = useContext(Context);

  const refreshAccessToken = async () => {
    const token = state.jwt;
    const refreshToken = state.refreshToken;
    if (!refreshToken) {
      dispatch({ type: "SET_LOGGED", payload: null });
      return null;
    }

    const refreshExpiresAt = state.refreshTokenExpiresAt;
    if (refreshExpiresAt) {
      const refreshExpMs = new Date(refreshExpiresAt).getTime();
      if (!Number.isNaN(refreshExpMs) && refreshExpMs < Date.now()) {
        dispatch({ type: "SET_LOGGED", payload: null });
        return null;
      }
    }

    return performRefresh(token, refreshToken, dispatch);
  };

  const getToken = async () => {
    const token = state.jwt;
    if (!token) return null;

    const decoded = state.jwtDecoded;
    const nowSec = Math.floor(Date.now() / 1000);
    const accessExpired = decoded?.exp && decoded.exp - nowSec < EXPIRY_BUFFER_SECONDS;

    if (!accessExpired) return token;

    return refreshAccessToken();
  };

  return { getToken, refreshAccessToken };
}
